import React, { useState, useEffect } from 'react';
import {
    Table,
    TableHead,
    TableBody,
    TableCell,
    TableRow,
    Grid,
    CircularProgress,
    TableContainer
} from '@material-ui/core';
import { useSwaggerClient } from '../../../hooks/useSwaggerClient';
import ActionIconButton from '../../../components/atom/ActionIconButton';
import AdminHeader from '../../../components/atom/AdminHeader';
import { EditIcon } from '../../../icons';
import TablePager from '../../../components/atom/TablePager';
import { useSorting } from '../../../hooks/useSorting';
import SortableTableCell from '../../../components/atom/SortableTableCell';
import { useHistory, useLocation } from 'react-router-dom/cjs/react-router-dom.min';
import { useCallback } from 'react';
import FilterLine from '../../../components/filter/FilterLine';
import { Alert } from '@material-ui/lab';
import moment from 'moment';

const EXPIRING_DOCS_FILTERS = [
    {
        key: 'expiring',
        label: 'Expiring in',
        type: 'expiringSelect'
    },
    {
        key: 'vendor',
        label: 'Vendor name',
        type: 'vendorautocomplete'
    },
    {
        key: 'expireBefore',
        label: 'Expires before',
        type: 'date'
    },
    {
        key: 'expireAfter',
        label: 'Expires after',
        type: 'date'
    }
];

function getFilterFromUrl(urlSearchPart) {
    const searchParams = new URLSearchParams(urlSearchPart.trim() !== '' ? urlSearchPart : '');

    const expireBefore =
        searchParams.get('expireBefore') !== null &&
        moment(searchParams.get('expireBefore')).isValid()
            ? moment(searchParams.get('expireBefore'))
            : null;
    const expireAfter =
        searchParams.get('expireAfter') !== null &&
        moment(searchParams.get('expireAfter')).isValid()
            ? moment(searchParams.get('expireAfter'))
            : null;

    return {
        expiring: searchParams.get('expiring') || '30',
        vendor: searchParams.get('vendor') || null,
        expireBefore,
        expireAfter
    };
}

function getUrlFromFilter(filter) {
    const search = new URLSearchParams();

    if (filter.vendor) {
        search.append('vendor', filter.vendor);
    }
    if (filter.expiring) {
        search.append('expiring', filter.expiring);
    }
    if (filter.expireBefore) {
        search.append('expireBefore', filter.expireBefore.format('YYYY-MM-DD'));
    }
    if (filter.expireAfter) {
        search.append('expireAfter', filter.expireAfter.format('YYYY-MM-DD'));
    }

    const searchString = search.toString();
    return `?${searchString}`;
}

function ExpiringDocumentsPage() {
    const [page, setPage] = useState(0);
    const [pageSize, setPageSize] = useState(10);
    const { sort, sortDir, setSorting } = useSorting(['end_date', 'ASC']);
    const location = useLocation();
    const history = useHistory();
    const [currentFilters, setFilters] = useState(getFilterFromUrl(location.search));

    const setCurrentFilters = useCallback(
        newfilters => history.replace(getUrlFromFilter(newfilters)),
        [history]
    );

    useEffect(() => {
        setFilters(getFilterFromUrl(location.search));
    }, [location.search, setFilters]);

    const [data, loading, error] = useSwaggerClient(
        'documents',
        'getExpiringDocuments',
        {
            page,
            pageSize,
            sort,
            sortDir,
            vendor: currentFilters.vendor,
            expiring: currentFilters.expiring,
            expireBefore: moment(currentFilters.expireBefore).isValid()
                ? moment(currentFilters.expireBefore).format('YYYY-MM-DD')
                : '',
            expireAfter: moment(currentFilters.expireAfter).isValid()
                ? moment(currentFilters.expireAfter).format('YYYY-MM-DD')
                : ''
        },
        [page, pageSize]
    );

    const resetFilters = () =>
        setCurrentFilters({
            vendor: null,
            expiring: '30',
            expireBefore: null,
            expireAfter: null
        });

    useEffect(() => {
        if (loading) {
            return;
        }
        if (data?.documents.length === 0 && page !== 0) {
            setPage(0);
        }
    }, [page, data, loading, setPage]);

    if (error) {
        return (
            <Grid container justifyContent="center">
                <Alert className="mt-2" severity="error">
                    <h4>{error.response.body.message}</h4>
                </Alert>
            </Grid>
        );
    }

    return (
        <Grid container className="bg-white client-table page" alignItems="center" spacing={2}>
            <AdminHeader title="Expiring Documents" />
            <Grid item xs={12}>
                <FilterLine
                    possibleFilters={EXPIRING_DOCS_FILTERS}
                    filters={currentFilters}
                    setFilters={setCurrentFilters}
                    resetFilters={resetFilters}
                />
            </Grid>
            <Grid item xs={12}>
                {loading && data?.documents.length === 0 ? (
                    <CircularProgress />
                ) : (
                    <TableContainer>
                        <Table>
                            <TableHead>
                                <TableRow>
                                    <SortableTableCell
                                        arrow={sort === 'vendor' ? sortDir : ''}
                                        onClick={() => setSorting('vendor')}
                                    >
                                        Vendor Name
                                    </SortableTableCell>
                                    <SortableTableCell
                                        arrow={sort === 'end_date' ? sortDir : ''}
                                        onClick={() => setSorting('end_date')}
                                    >
                                        Expiration Date
                                    </SortableTableCell>
                                    <TableCell>Document</TableCell>
                                    <TableCell>Actions</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {data?.documents.map(doc => (
                                    <TableRow key={doc.id}>
                                        <TableCell>{doc.company_name}</TableCell>
                                        <TableCell>{doc.end_date}</TableCell>
                                        <TableCell>
                                            <a
                                                href={doc.name}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                            >
                                                {doc.type}
                                            </a>
                                        </TableCell>
                                        <TableCell style={{ whiteSpace: 'nowrap' }}>
                                            <ActionIconButton
                                                title="Edit"
                                                icon={EditIcon}
                                                to={`/admin/client/${doc.company_id}/documents/`}
                                            />
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                )}
                <Grid container justifyContent="flex-end">
                    <TablePager
                        total={data?.total}
                        page={page}
                        setPage={setPage}
                        pageSize={pageSize}
                        setPageSize={setPageSize}
                    />
                </Grid>
            </Grid>
        </Grid>
    );
}

export default ExpiringDocumentsPage;
